import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TourId } from 'webapp/enums/tour';

import type DesignerController from 'webapp/controllers/designer';

import type DesignerOptionHiddenTour from 'webapp/models-custom/tour/designer/option-hidden';

import FlowTourBaseService from 'webapp/services/flow/tour/base';

export enum DesignerTourLocation {
	LEFT_PANEL = 'left-panel',
	RIGHT_PANEL = 'right-panel',
	BOARD_MORE_MENU = 'board-more-menu',
	BOARD_SETTINGS = 'board-settings',
	LOOP_BOX = 'loop-box',
	OTHER = 'other',
}

export interface DesignerTourSettings {
	id: TourId;
	checkStartConditions: () => boolean;
}

export interface DesignerPanelTourSettings extends DesignerTourSettings {
	showHiddenOption: () => void;
}

interface DesignerTourLocationSettings {
	id: TourId;
	location: DesignerTourLocation;
}

export default abstract class FlowTourDesignerService extends FlowTourBaseService {
	@service('navigation') declare navigationService: Service.Navigation;

	hiddenOptionTour: DesignerOptionHiddenTour | null = null;
	explicitlyClosedSteps: number[] = [];

	orderedTourSettings: DesignerTourLocationSettings[] = [
		{ id: TourId.DESIGNER__INTRO, location: DesignerTourLocation.OTHER },
		{ id: TourId.DESIGNER__DRAG_N_DROP, location: DesignerTourLocation.LEFT_PANEL },
		{ id: TourId.DESIGNER__RESIZE_BLOCK, location: DesignerTourLocation.BOARD_SETTINGS },
		{ id: TourId.DESIGNER__MOVE_BLOCK, location: DesignerTourLocation.BOARD_SETTINGS },
		{ id: TourId.DESIGNER__SELECT_VISUALIZATION, location: DesignerTourLocation.LEFT_PANEL },
		{ id: TourId.DESIGNER__CHANGE_DATE_RANGE, location: DesignerTourLocation.RIGHT_PANEL },
		{ id: TourId.DESIGNER__SHARABLE_LINK, location: DesignerTourLocation.BOARD_MORE_MENU },
		{ id: TourId.DESIGNER__MORE_OPTION, location: DesignerTourLocation.BOARD_MORE_MENU },
		{ id: TourId.DESIGNER__ADD_TO_LOOP, location: DesignerTourLocation.LOOP_BOX },
		{ id: TourId.DESIGNER__ADD_TO_REPORT, location: DesignerTourLocation.BOARD_MORE_MENU },
	];

	@cached
	get orderedTourIds(): TourId[] {
		return this.orderedTourSettings.map((tour) => tour.id);
	}

	private getTourIdsByLocation(location: DesignerTourLocation): TourId[] {
		return this.orderedTourSettings
			.filter((tour) => tour.location === location)
			.map((tour) => tour.id);
	}

	// we want to disable designer tour for board account templates
	get isBoardAccountTemplate(): boolean {
		const { board } = this.navigationService.appRoute.controllerFor(
			'designer',
		) as DesignerController;

		return board ? board.isAccountTemplate : false;
	}

	override checkGeneralEligibility(): boolean {
		const routePath = String(this.navigationService.activatedRoute);
		return (
			super.checkGeneralEligibility() &&
			routePath.startsWith('designer') &&
			this.currentUserService.deviceString === 'desktop' &&
			!this.isBoardAccountTemplate
		); // prevent showing designer tour on mobile device or when user account template is opened
	}

	checkHiddenTourEligibility(step: number): boolean {
		return !this.hiddenOptionTour && !this.explicitlyClosedSteps.includes(step);
	}

	markHiddenTourAsExplicitlyClosed(step: number): void {
		if (this.checkHiddenTourEligibility(step)) {
			this.explicitlyClosedSteps.push(step);
		}
	}

	resetHiddenTourExplicitlyClosedStatus(currentStep: number): void {
		this.explicitlyClosedSteps = this.explicitlyClosedSteps.filter(
			(step) => step !== currentStep,
		);
	}

	startHiddenTour(step: number): void {
		if (this.checkHiddenTourEligibility(step)) {
			this.hiddenOptionTour = this.tourService.initTour(TourId.DESIGNER__BOARD_MORE_MENU);
			this.hiddenOptionTour?.start(step);
		}
	}

	endHiddenTour(): void {
		this.hiddenOptionTour?.end();
		this.hiddenOptionTour = null;
	}

	endActiveTourAtLocation(location: DesignerTourLocation): void {
		const activeTour = this.activeTour,
			leftPanelToursIds = this.getTourIdsByLocation(location);

		if (leftPanelToursIds.includes(activeTour?.id as TourId)) {
			this.endTour(activeTour?.id as TourId);
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		'flow/tour/designer': FlowTourDesignerService;
	}
}
